import { ProgressBar, Badge, Alert } from '@dreamtree-org/twreact-ui';
import { SectionCard, KeyValue, StatCard, QueryState, StatusBadge } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { formatDate } from '@/lib/format';
import { LineChart } from '@dreamtree-org/twreact-ui/charts';
import { formatDateTime } from '../../lib/format';

/**
 * Isolated database: the connection row, plus the health probe the sweep runs every
 * few minutes (round trip, size, tables from information_schema). Pool use is this
 * API process's pool. No backup job is configured here, and the page says so.
 */
export default function DatabaseTab({ merchant: m }) {
  const c = m.connection;
  const metrics = useQuery('platformMerchantMetrics', { id: m.id }, { select: (r) => r.data });
  return (
    <div className="space-y-4">
      <SectionCard title="Connection" actions={<Badge variant="success" size="sm">Live</Badge>}>
        <KeyValue columns={2} items={[
          { label: 'Database', value: <code className="text-xs">{m.database_name}</code> },
          { label: 'Engine', value: c && c.engine },
          { label: 'Status', value: c && c.status },
          { label: 'Connection version', value: c && `v${c.connection_version}` },
          { label: 'Encryption key', value: c && c.key_id },
          { label: 'Schema version', value: c && c.schema_version },
          { label: 'Updated', value: c && formatDate(c.updated_at) },
        ]} />
        <p className="text-xs opacity-60">The connection URL is stored only as an encrypted envelope and is never sent to the browser.</p>
      </SectionCard>

      <QueryState query={metrics}>
        {(d) => {
          const s = d.sample;
          return (
            <>
              {s && !s.ok ? <Alert variant="error" title="The last probe failed">{s.error}</Alert> : null}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Health" value={<StatusBadge status={d.health} />} hint={s ? `probed ${formatDateTime(s.at)}` : 'not probed yet'} />
                <StatCard label="Size" value={s && s.size_mb != null ? `${s.size_mb} MB` : '—'} hint={s && s.tables != null ? `${s.tables} tables · ~${(s.rows_estimate || 0).toLocaleString('en-IN')} rows` : ''} />
                <StatCard label="Round trip" value={s && s.probe_ms != null ? (s.probe_ms < 1 ? '< 1 ms' : `${s.probe_ms} ms`) : '—'} hint="median of 3 pings" />
                <StatCard label="Backups" value="Not configured" hint="no backup job is set up for this database" />
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                <SectionCard title="Schema & last job">
                  <KeyValue items={[
                    { label: 'Schema', value: d.schema ? `${d.schema.version} (${d.schema.status})` : '—' },
                    { label: 'Schema updated', value: d.schema ? formatDateTime(d.schema.updated_at) : '—' },
                    { label: 'Last job', value: d.last_job ? `${d.last_job.operation} · ${d.last_job.status}` : '—' },
                    { label: 'Job finished', value: d.last_job ? `${formatDateTime(d.last_job.at)} (${d.last_job.duration_s} s)` : '—' },
                  ]} />
                  {d.pool ? (
                    <div className="space-y-1">
                      <div className="text-xs opacity-60">Pool in use (this API process): {d.pool.used} / {d.pool.max}</div>
                      <ProgressBar value={d.pool.used} max={d.pool.max} size="sm" ariaLabel="Pool utilisation" />
                    </div>
                  ) : <p className="text-xs opacity-60">No open pool in this API process yet.</p>}
                </SectionCard>
                <SectionCard title="Probe round trip · last 24 h (ms)">
                  {d.latency.length ? (
                    <LineChart title="Database probe round trip by hour" data={d.latency} index="hour" categories={['probe_ms']} valueFormatter={(v) => `${v} ms`} showLegend={false} height={220} />
                  ) : <p className="text-sm opacity-70">No probes in the last 24 hours. The sweep records one on every run.</p>}
                </SectionCard>
              </div>
            </>
          );
        }}
      </QueryState>
    </div>
  );
}
